// frontend/src/components/public/blocks/ContactFormBlock.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  MenuItem
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import axios from 'axios';

interface ContactFormBlockProps {
  data: {
    fields: Array<{
      name: string;
      type: 'text' | 'email' | 'textarea' | 'select';
      label: string;
      required: boolean;
      options?: string[];
    }>;
    submitText: string;
    webhookUrl?: string;
  };
  theme?: {
    primaryColor: string;
    accentColor: string;
    logo?: string;
  };
  portfolioTitle: string; // NEW PROP
}

const ContactFormBlock: React.FC<ContactFormBlockProps> = ({ data, theme, portfolioTitle }) => {
  const { fields = [], submitText = 'Send Message' } = data;
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accentColor = theme?.accentColor || '#FF0000';
  const primaryColor = theme?.primaryColor || '#000000';
  const logo = theme?.logo;

  const handleChange = (fieldName: string, value: string) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      // Validate required fields
      const missingFields = fields
        .filter((field) => field.required && !formData[field.name])
        .map((field) => field.label);

      if (missingFields.length > 0) {
        throw new Error(`Please fill in: ${missingFields.join(', ')}`);
      }

      // Send to backend API
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || ''}/api/email/portfolio-contact`,
        {
          portfolioTitle,
          formData,
          testMode: false
        }
      );

      if (response.data.success) {
        setSuccess(true);
        setFormData({}); // Clear form
        
        // Scroll to success message
        setTimeout(() => {
          const successElement = document.getElementById('contact-success');
          if (successElement) {
            successElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else {
        throw new Error(response.data.message || 'Failed to send message');
      }
    } catch (err: any) {
      console.error('Contact form error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (fields.length === 0) {
    return null;
  }

  const textFieldStyles = {
    '& .MuiInputLabel-root': { 
      color: '#666666',
      '&.Mui-focused': { color: accentColor }
    },
    '& .MuiOutlinedInput-root': {
      color: '#333333',
      '& fieldset': { borderColor: '#cccccc' },
      '&:hover fieldset': { borderColor: accentColor },
      '&.Mui-focused fieldset': { borderColor: accentColor }
    },
    '& .MuiOutlinedInput-input': {
      color: '#333333'
    }
  };

  return (
    <Box
      id="contact"
      sx={{
        py: { xs: 6, md: 10 },
        px: { xs: 3, md: 6 },
        maxWidth: '700px',
        mx: 'auto'
      }}
    >
      {/* Logo Display */}
      {logo && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 4
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{
              maxHeight: 80,
              maxWidth: 200,
              objectFit: 'contain'
            }}
          />
        </Box>
      )}

      <Typography
        variant="h3"
        sx={{
          fontSize: { xs: '1.75rem', md: '2.25rem' },
          fontWeight: 700,
          color: primaryColor,
          mb: 1,
          textAlign: 'center'
        }}
      >
        Get in Touch
      </Typography>

      <Typography
        variant="body1"
        sx={{
          fontSize: '1rem',
          color: '#666666',
          mb: 4,
          textAlign: 'center'
        }}
      >
        We'd love to hear from you. Fill out the form below and we'll get back to you soon.
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        {fields.map((field) => {
          if (field.type === 'textarea') {
            return (
              <TextField
                key={field.name}
                fullWidth
                multiline
                rows={4}
                label={field.label}
                value={formData[field.name] || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
                required={field.required}
                variant="outlined"
                margin="normal"
                sx={{ ...textFieldStyles, mb: 2 }}
              />
            );
          }

          if (field.type === 'select') {
            return (
              <TextField
                key={field.name}
                fullWidth
                select
                label={field.label}
                value={formData[field.name] || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
                required={field.required}
                variant="outlined"
                margin="normal"
                sx={{ ...textFieldStyles, mb: 2 }}
              >
                {field.options?.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            );
          }

          return (
            <TextField
              key={field.name}
              fullWidth
              type={field.type}
              label={field.label}
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              required={field.required}
              variant="outlined"
              margin="normal"
              sx={{ ...textFieldStyles, mb: 2 }}
            />
          );
        })}

        {success && (
          <Alert 
            id="contact-success"
            severity="success" 
            sx={{ mb: 2 }}
          >
            ✅ Thank you! Your message has been sent successfully. We'll be in touch soon.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
          sx={{
            bgcolor: accentColor,
            color: 'white',
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              bgcolor: accentColor,
              opacity: 0.9
            },
            '&:disabled': {
              bgcolor: '#cccccc',
              color: '#666666'
            }
          }}
        >
          {submitting ? 'Sending...' : submitText}
        </Button>
      </Box>
    </Box>
  );
};

export default ContactFormBlock;